import { useMemo, useState } from 'react';

import { ChevronDown, Search, Star } from 'lucide-react';

import { MOCK_SERIES } from '../../../mocks/luminaireAnalytics';
import { MockLuminaire, ProjectType } from '../../../types/analytics';
import { LampThumb } from './LampThumb';

interface LuminaireListProps {
  luminaires: MockLuminaire[];
  slotAId: string | null;
  slotBId: string | null;
  onPick: (luminaire: MockLuminaire) => void;
}

type SortKey = 'compatibility' | 'matches' | 'apply' | 'favorite' | 'gap' | 'recent' | 'alpha';
type UsageFilter = 'all' | 'used' | 'never';

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'compatibility', label: 'Compatibility (high → low)' },
  { value: 'matches', label: 'Match frequency (high → low)' },
  { value: 'apply', label: 'Apply rate (high → low)' },
  { value: 'favorite', label: 'Favorite rate (high → low)' },
  { value: 'gap', label: 'Optimal gap (low → high)' },
  { value: 'recent', label: 'Recently added' },
  { value: 'alpha', label: 'Alphabetical (A → Z)' },
];

const PROJECT_TYPE_OPTIONS: { value: ProjectType; label: string }[] = [
  { value: 'road', label: 'Road' },
  { value: 'sidewalk', label: 'Sidewalk' },
  { value: 'pedestrian', label: 'Pedestrian' },
  { value: 'park', label: 'Park' },
  { value: 'parking_lot', label: 'Parking lot' },
  { value: 'intersection', label: 'Intersection' },
  { value: 'roundabout', label: 'Roundabout' },
  { value: 'highway', label: 'Highway' },
  { value: 'square', label: 'Square' },
  { value: 'tunnel', label: 'Tunnel' },
];

export const LuminaireList = ({ luminaires, slotAId, slotBId, onPick }: LuminaireListProps) => {
  const [seriesId, setSeriesId] = useState<string>('all');
  const [usage, setUsage] = useState<UsageFilter>('used');
  const [sort, setSort] = useState<SortKey>('compatibility');
  const [search, setSearch] = useState('');
  const [activeTypes, setActiveTypes] = useState<Set<ProjectType>>(new Set());
  const [sortOpen, setSortOpen] = useState(false);

  const filtered = useMemo(() => {
    let list = luminaires;
    if (seriesId !== 'all') list = list.filter((l) => l.seriesId === seriesId);
    if (usage === 'used') list = list.filter((l) => l.analytics.matchFrequencyTotal > 0);
    if (usage === 'never') list = list.filter((l) => l.analytics.matchFrequencyTotal === 0);
    if (activeTypes.size > 0) {
      list = list.filter((l) => l.analytics.projectTypesCovered.some((t) => activeTypes.has(t)));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (l) =>
          l.name.toLowerCase().includes(q) || l.sku.toLowerCase().includes(q) || l.opticCode.toLowerCase().includes(q),
      );
    }
    const sorted = [...list];
    switch (sort) {
      case 'compatibility':
        sorted.sort((a, b) => b.analytics.compatibilityScoreAvg - a.analytics.compatibilityScoreAvg);
        break;
      case 'matches':
        sorted.sort((a, b) => b.analytics.matchFrequencyTotal - a.analytics.matchFrequencyTotal);
        break;
      case 'apply':
        sorted.sort((a, b) => b.analytics.applyRatePercent - a.analytics.applyRatePercent);
        break;
      case 'favorite':
        sorted.sort((a, b) => b.analytics.favoriteRatePercent - a.analytics.favoriteRatePercent);
        break;
      case 'gap':
        sorted.sort((a, b) => a.analytics.optimalGapPercent - b.analytics.optimalGapPercent);
        break;
      case 'recent':
        sorted.sort((a, b) => +new Date(b.analytics.firstSeenAt) - +new Date(a.analytics.firstSeenAt));
        break;
      case 'alpha':
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
    }
    return sorted;
  }, [luminaires, seriesId, usage, activeTypes, search, sort]);

  const toggleType = (t: ProjectType) => {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  };

  return (
    <div className="h-full flex flex-col bg-greyP5 border-r border-greyP4">
      {/* Filters */}
      <div className="p-3 border-b border-greyP4 space-y-3 max-h-[60%] overflow-y-auto">
        {/* Search */}
        <div className="flex items-center bg-blackP1 border border-greyP4 rounded-lg px-2.5">
          <Search size={14} className="text-greyP3" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search SKU, name, optic…"
            className="bg-transparent outline-none flex-1 px-2 py-2 text-sm text-white placeholder:text-greyP3"
          />
        </div>

        {/* Series */}
        <div>
          <label className="text-[10px] uppercase tracking-wider text-greyP2 font-semibold">Series</label>
          <select
            value={seriesId}
            onChange={(e) => setSeriesId(e.target.value)}
            className="mt-1 w-full bg-blackP1 border border-greyP4 rounded-lg px-2.5 py-2 text-sm text-white outline-none hover:border-green01/50 focus:border-green01"
          >
            <option value="all">All series</option>
            {MOCK_SERIES.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        {/* Usage toggle */}
        <div>
          <label className="text-[10px] uppercase tracking-wider text-greyP2 font-semibold">Usage</label>
          <div className="mt-1 flex items-center bg-blackP1 border border-greyP4 rounded-lg p-0.5">
            {(['all', 'used', 'never'] as UsageFilter[]).map((u) => (
              <button
                key={u}
                onClick={() => setUsage(u)}
                className={`flex-1 px-2 py-1.5 rounded-md text-[11px] font-medium transition-colors ${
                  usage === u ? 'bg-greyP4 text-green01' : 'text-greyP2 hover:text-white'
                }`}
              >
                {u === 'all' ? 'All' : u === 'used' ? 'Used' : 'Never used'}
              </button>
            ))}
          </div>
        </div>

        {/* Project type chips */}
        <div>
          <label className="text-[10px] uppercase tracking-wider text-greyP2 font-semibold">Project type</label>
          <div className="mt-1 flex flex-wrap gap-1">
            {PROJECT_TYPE_OPTIONS.map((t) => {
              const active = activeTypes.has(t.value);
              return (
                <button
                  key={t.value}
                  onClick={() => toggleType(t.value)}
                  className={`text-[10px] px-2 py-1 rounded-full border transition-colors ${
                    active
                      ? 'bg-green01/15 border-green01 text-green01'
                      : 'bg-blackP1 border-greyP4 text-greyP2 hover:text-white hover:border-greyP3'
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Sort */}
        <div className="relative">
          <label className="text-[10px] uppercase tracking-wider text-greyP2 font-semibold">Sort by</label>
          <button
            onClick={() => setSortOpen((o) => !o)}
            className="mt-1 w-full flex items-center justify-between bg-blackP1 border border-greyP4 rounded-lg px-2.5 py-2 text-sm text-white hover:border-green01/50"
          >
            <span>{SORT_OPTIONS.find((o) => o.value === sort)?.label}</span>
            <ChevronDown size={14} className="text-greyP3" />
          </button>
          {sortOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setSortOpen(false)} />
              <div className="absolute left-0 right-0 mt-1 bg-greyP5 border border-greyP4 rounded-lg shadow-xl py-1 z-50">
                {SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      setSort(opt.value);
                      setSortOpen(false);
                    }}
                    className={`w-full text-left text-xs px-3 py-2 hover:bg-greyP4 ${
                      sort === opt.value ? 'text-green01' : 'text-white'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-greyP2 border-b border-greyP4 bg-blackP1/40 sticky top-0 z-10">
          {filtered.length} luminaire{filtered.length === 1 ? '' : 's'}
        </div>
        {filtered.length === 0 && (
          <div className="p-6 text-center text-sm text-greyP2">No luminaires match these filters.</div>
        )}
        {filtered.map((l) => {
          const isA = slotAId === l.id;
          const isB = slotBId === l.id;
          const leftBorder = isA || isB ? '#B3EC49' : 'transparent';
          return (
            <button
              key={l.id}
              onClick={() => onPick(l)}
              className="w-full text-left px-3 py-3 border-b border-greyP4/60 hover:bg-greyP4/40 transition-colors flex gap-3 items-start"
              style={{ borderLeft: `3px solid ${leftBorder}` }}
            >
              <LampThumb luminaire={l} size={44} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white font-medium truncate">{l.name}</div>
                <div className="text-[10px] text-greyP2 mt-0.5 truncate">
                  {l.product} · {l.opticCode}
                </div>
                <div className="flex items-center gap-3 mt-1.5 text-[10px]">
                  <span className="text-green01 font-semibold">{l.analytics.compatibilityScoreAvg.toFixed(0)}%</span>
                  <span className="text-greyP2">{l.analytics.matchFrequencyTotal} m</span>
                  <span className="text-greyP2 flex items-center gap-0.5">
                    <Star size={9} className="text-orange-400" />
                    {l.analytics.favoriteCount}
                  </span>
                </div>
              </div>
              {isA && (
                <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm bg-green01 text-black">A</span>
              )}
              {isB && (
                <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm border border-green01 text-green01">
                  B
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
